import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getRowsPerPageCache, setRowsPerPageCache } from '../utils/paginaRowsCache';

// No celular (< 600px, mesmo breakpoint 'sm' do tema) a navegação é uma barra
// fixa embaixo (ver AppMobile.tsx, ALTURA_BARRA_MOBILE = 64 + respiro) que
// cobre o pé da janela — sem descontar ela, as últimas linhas e a paginação
// ficavam escondidas atrás da barra.
const reservaBarraMobile = () => (window.innerWidth < 600 ? 80 : 0);

export interface IUseListagemAlturaParams {
  cacheKey: string;
  // Usado só quando não há nada em cache ainda (primeiro acesso à tela).
  valorInicial: number;
  // 'constante': altura de linha fixa conhecida de antemão (ex.: nenhuma coluna quebra linha) —
  // mais barato, não depende de dados já terem chegado. 'medido': mede a altura real de
  // cabeçalho/linha/rodapé no DOM — necessário quando o conteúdo da linha varia de altura.
  footerMode?: 'constante' | 'medido';
  // Obrigatório no modo 'constante'.
  rowHeight?: number;
  // Modo 'constante': reserva fixa pro rodapé (paginação + seletor de linhas por página), no
  // seu estado mais alto — nunca medida a partir do <tfoot> real porque a paginação só existe
  // no DOM quando totalCount > rowsPerPage, o que criaria uma dependência circular.
  footerReservado?: number;
  bottomGap?: number;
  minRows?: number;
  maxRows?: number;
  folgaLinhas?: number;
  // Modo 'medido': valor de "algo mudou, remedir" — passar `totalCount`, não `rows.length`
  // (mudar a referência do array de linhas a cada render/refetch causaria loop de recálculo).
  recalcDep?: number;
  // Modo 'medido': espera esse tempo depois de `recalcDep` mudar antes de remedir, dando tempo
  // do DOM da nova página assentar.
  settleDelayMs?: number;
}

export interface IUseListagemAlturaResult {
  tabelaRef: React.RefObject<HTMLDivElement>;
  containerHeight: number | undefined;
  rowsPerPage: number;
  // Marca a escolha como manual (trava o recálculo automático no resize) além de atualizar
  // estado + cache — usar no onChange do seletor "X / página" do rodapé.
  setRowsPerPage: (valor: number) => void;
}

// Calcula quantas linhas cabem no espaço disponível até o rodapé da janela, evitando tanto
// scroll na página inteira (a listagem sempre mede o espaço real, não usa um `calc(100vh-Npx)`
// fixo que se desalinha conforme o header/toolbar da tela) quanto scroll interno na tabela
// assim que a paginação aparece (a reserva de rodapé cobre o estado mais alto do rodapé, com
// paginação + seletor de linhas visíveis).
export function useListagemAltura(params: IUseListagemAlturaParams): IUseListagemAlturaResult {
  const {
    cacheKey,
    valorInicial,
    footerMode = 'constante',
    rowHeight,
    footerReservado = 68,
    bottomGap = 24,
    minRows = 1,
    maxRows = 100,
    folgaLinhas = 1,
    recalcDep,
    settleDelayMs = 50,
  } = params;

  const tabelaRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>();
  const [rowsPerPage, setRowsPerPageState] = useState(() => getRowsPerPageCache(cacheKey) ?? valorInicial);
  // Uma vez que o usuário escolhe manualmente a quantidade por página, o cálculo automático
  // pelo tamanho da tela para de sobrescrever a escolha dele a cada resize/remedição.
  const ajusteManual = useRef(false);
  const rowHeightMedidoRef = useRef(0);
  const headerHeightRef = useRef(0);
  const footerHeightRef = useRef(0);

  const setRowsPerPage = (valor: number) => {
    ajusteManual.current = true;
    setRowsPerPageCache(cacheKey, valor);
    setRowsPerPageState(valor);
  };

  // --- Modo 'constante' ---
  const calcularLimiteConstante = () => {
    if (ajusteManual.current) return;
    const container = tabelaRef.current;
    if (!container || rowHeight === undefined) return;

    // Folga a mais que o necessário: getBoundingClientRect() retorna valores fracionários que
    // podem arredondar pra cima ao aplicar como `height` em CSS — sem essa margem, o container
    // fica 1px mais alto que o espaço real, o suficiente pra criar scroll na página inteira.
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - bottomGap - reservaBarraMobile());
    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? headerEl.getBoundingClientRect().height : 34;
    const alturaDisponivel = alturaContainer - alturaHeader - footerReservado;
    const novoLimite = Math.min(
      maxRows,
      Math.max(minRows, Math.floor(alturaDisponivel / rowHeight) - folgaLinhas)
    );

    setContainerHeight((prev) => (prev === alturaContainer ? prev : alturaContainer));
    setRowsPerPageCache(cacheKey, novoLimite);
    setRowsPerPageState((prev) => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect (não useEffect): roda de forma síncrona antes da pintura e, mais
  // importante, antes do efeito de busca da tela (que depende de rowsPerPage) disparar a
  // requisição inicial — a primeira busca já sai com o limite correto.
  useLayoutEffect(() => {
    if (footerMode === 'constante') calcularLimiteConstante();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (footerMode !== 'constante') return;
    window.addEventListener('resize', calcularLimiteConstante);
    return () => window.removeEventListener('resize', calcularLimiteConstante);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerMode]);

  // --- Modo 'medido' ---
  const calcularAlturaContainerMedido = () => {
    const container = tabelaRef.current;
    if (!container) return;
    const novaAltura = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - bottomGap - reservaBarraMobile());
    setContainerHeight((prev) => (prev === novaAltura ? prev : novaAltura));
  };

  const calcularLimiteMedido = () => {
    if (ajusteManual.current) return;
    const container = tabelaRef.current;
    if (!container) return;

    const headerEl = container.querySelector('thead');
    const rowEl = container.querySelector('tbody tr');
    const footerEl = container.querySelector('tfoot');

    if (headerEl) headerHeightRef.current = headerEl.getBoundingClientRect().height;
    if (rowEl) rowHeightMedidoRef.current = rowEl.getBoundingClientRect().height;
    if (footerEl) footerHeightRef.current = footerEl.getBoundingClientRect().height;

    if (!rowHeightMedidoRef.current || !containerHeight) return;

    // Usa o orçamento de altura já calculado (containerHeight), não container.clientHeight:
    // quando a listagem tem poucos itens o container encolhe (height:'auto'), e clientHeight
    // passaria a refletir só o conteúdo atual em vez do espaço disponível.
    const alturaDisponivel = containerHeight - headerHeightRef.current - footerHeightRef.current;
    const novoLimite = Math.min(
      maxRows,
      Math.max(minRows, Math.floor(alturaDisponivel / rowHeightMedidoRef.current) - folgaLinhas)
    );

    setRowsPerPageCache(cacheKey, novoLimite);
    setRowsPerPageState((prev) => (prev === novoLimite ? prev : novoLimite));
  };

  useEffect(() => {
    if (footerMode !== 'medido') return;
    calcularAlturaContainerMedido();
    window.addEventListener('resize', calcularAlturaContainerMedido);
    return () => window.removeEventListener('resize', calcularAlturaContainerMedido);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerMode]);

  useEffect(() => {
    if (footerMode !== 'medido') return;
    const id = setTimeout(calcularLimiteMedido, settleDelayMs);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerMode, recalcDep, containerHeight]);

  return { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage };
}
